import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Loading, Modal, Text, Button, Tooltip, Textarea } from '@nextui-org/react';
import { AgGridReact } from "ag-grid-react";
import Papa from "papaparse";

import endpoint from "../../config/endpoint.json";
import { mstore } from "../../store";
import { fetchWithJson } from "../../lib/api";
import { error, success, warning } from "../../lib/userAlerts";
import dayjs from '../../lib/day';
import checkAccess from '../../lib/checkAccess';

import ReimbursableRecipetsDetails from "./ReimbursableRecipetsDetails";
import FilterReceipt from "../../components/PageSelectionComponent/FilterReceipt";
import SubPageHeader from "../../components/SubPageHeader";

const APPROVED = "Approved";
const REJECTED = "Rejected";
const RESET = "New";
const cellStyle = {textAlign: "center"};

const createColumnDefs = () => [
  {
    headerName: "Receipt Number",
    field: "receipt_number",
    filter: "agNumberColumnFilter",
    headerClass: 'tc',
    cellStyle: cellStyle,
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
    width: 90,
    resizable: true,
  },
  {
    headerName: "Order Number",
    field: "order_id",
    filter: "agTextColumnFilter",
    headerClass: 'tc',
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
    width: 150,
    resizable: true,
  },
  {
    headerName: "Status",
    field: "status",
    filter: "agTextColumnFilter",
    headerClass: 'tc',
    width: 90,
    resizable: true,
    cellClassRules: {
      "bg-light-red": function (params) {
        return params.value === REJECTED;
      },
      "bg-light-green": function (params) {
        return params.value === APPROVED;
      },
    },
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
  },
  {
    headerName: "Merchant Name",
    field: "driver_merchant_name",
    headerClass: 'tc',
    suppressMenu: true,
    minWidth: 180,
    resizable: true,
  },
  { 
    headerName: "Date Entered", 
    field: "date_entered",
    headerClass: 'tc',
    minWidth: 120,
    resizable: true,
  },
  {
    headerName: "Employee Id",
    field: "employee_id",
    filter: "agTextColumnFilter",
    headerClass: 'tc',
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
    minWidth: 120,
    resizable: true,
  },
  {
    headerName: "Employee Name",
    field: "employee_name",
    filter: "agTextColumnFilter",
    headerClass: 'tc',
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
    minWidth: 150,
    resizable: true,
  },
  { 
    headerName: "OCR Date", 
    field: "ocr_date",
    headerClass: 'tc',
    minWidth: 120,
    resizable: true,
  },
  { 
    headerName: "OCR Total", 
    field: "ocr_total", 
    suppressMenu: true,
    cellStyle: cellStyle,
    headerClass: 'tc',
    minWidth: 90,
    resizable: true,
  },
  {
    headerName: "OCR Confidence",
    field: "ocr_confidence",
    suppressMenu: true,
    cellStyle: cellStyle,
    minWidth: 90,
    resizable: true,
  },
  { 
    headerName: "Employee Date", 
    field: "employee_date",
    headerClass: 'tc',
    minWidth: 120,
    resizable: true,
  },
  {
    headerName: "Employee Total",
    field: "employee_total",
    headerClass: 'tc',
    suppressMenu: true,
    minWidth: 120,
    cellStyle: cellStyle,
    resizable: true,
  },
  {
    headerName: "Date Match",
    field: "date_match",
    filter: "agTextColumnFilter",
    headerClass: 'tc',
    cellStyle: cellStyle,
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
    minWidth: 90,
    resizable: true,
  },
  {
    headerName: "Total Match",
    field: "total_match",
    filter: "agTextColumnFilter",
    headerClass: 'tc',
    cellStyle: cellStyle,
    filterParams: {
      applyButton: true,
      clearButton: true,
    },
    minWidth: 90,
    resizable: true,
  },
  { 
    headerName: "Final Date", 
    field: "final_date", 
    headerClass: 'tc',
    suppressMenu: true,
    minWidth: 120, 
    resizable: true,
  },
  { 
    headerName: "Final Total", 
    field: "final_amount", 
    suppressMenu: true,
    minWidth: 90, 
    cellStyle: cellStyle,
    resizable: true,
    headerClass: 'tc'
  },
];

const ReimbursableRecipetsManagement = () => {
  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  const Access = useSelector(state => state.navigation.Access);
  
  const gridApi = useRef(null);
  const columnApi = useRef(null);

  const [state, setState] = useState({
    receiptsListBackUp: [],
    receiptsList: [],
    receiptDataForCsv: [],
    normalReceipts: [],
    archiveReceipts: [],
    currentReceipt: {},
    selectedReceipt: [],
    isOpen: false,
    noOfFilter: 0,
    fetching: false,
    receiptReferenceNo: null,
    exp1: "",
    exp2: "",
    Approved: 0,
    Denyed: 0,
    New: 0,
    driverId: "",
    orderNo: "",
    DateMatch: 0,
    TotalMatch: 0,
    comment: "",
    columnDefs: createColumnDefs(),
    rowSelection: "multiple",
    showArchiveReceipt: false,
  });

  const onGridReady = useCallback((params) => {
    gridApi.current = params.api;
    columnApi.current = params.columnApi;
  }, []);

  const setStatusOnGrid = useCallback((
    receipt_number,
    status,
    final_date = null,
    final_amount = null
  ) => {
    const rowNode = gridApi.current.getRowNode(receipt_number);
    rowNode.setDataValue("status", status);
    rowNode.setDataValue("final_date", final_date);
    rowNode.setDataValue("final_amount", final_amount);
  }, []);

  const removeOCRMarchantAndAddress = useCallback((data) => {
    const csvList = data.map(item => ({
      date_entered: item.date_entered,
      date_match: item.date_match,
      employee_date: item.employee_date,
      employee_id: item.employee_id,
      employee_name: item.employee_name,
      employee_total: item.employee_total,
      driver_merchant_name: item.driver_merchant_name,
      final_amount: item.final_amount,
      final_date: item.final_date,
      ocr_confidence: item.ocr_confidence,
      ocr_date: item.ocr_date,
      ocr_total: item.ocr_total,
      order_id: item.order_id,
      receipt_number: item.receipt_number,
      status: item.status,
      stop_id: item.stop_id,
      stop_name: item.stop_name,
      total_match: item.total_match,
    }));
    setState(prev => ({ ...prev, receiptDataForCsv: csvList }));
  }, []);

  const fetchReceiptsList = useCallback((
    order_id = null,
    driver_id = null,
    date_match = null,
    tot_match = null,
    is_approved = null,
    is_rejected = null,
    is_new = null,
    receipt_id = null,
    start_date = null,
    end_date = null
  ) => {
    setState(prev => ({
      ...prev,
      fetching: true,
      receiptsList: [],
      normalReceipts: [],
      archiveReceipts: [],
    }));

    fetchWithJson(endpoint.dc_fetch_driver_receipts_portal, {
      company_id: clientId,
      order_id,
      driver_id,
      date_match,
      tot_match,
      is_approved,
      is_rejected,
      is_new,
      receipt_id,
      start_date,
      end_date,
    })
      .then(res => res.json())
      .then(data => {
        const archiveReceipts = data.filter(item => item.is_archived === 1);
        const normalReceipts = data.filter(item => item.is_archived !== 1);

        setState(prev => ({
          ...prev,
          receiptsList: prev.showArchiveReceipt ? archiveReceipts : normalReceipts,
          normalReceipts,
          archiveReceipts,
          receiptsListBackUp: data,
          fetching: false,
        }));
        removeOCRMarchantAndAddress(data);
      })
      .catch(err => {
        error("Oops! error occurred when fetching receipts");
        console.error(err);
      });
  }, [clientId, removeOCRMarchantAndAddress]);

  const handleApproved = useCallback(() => {
    if (state.selectedReceipt.length === 0) {
      warning("Please select a receipt");
      return;
    }

    state.selectedReceipt.forEach(items => {
      fetchWithJson(endpoint.dc_approve_receipt, {
        receipt_id: items.receipt_number,
        amount: items.employee_total,
        date: dayjs(items.employee_date, "DD-MMM-YYYY").format("YYYY-MM-DD"),
        comments: state.comment,
      })
        .then(() => {
          success(`Receipt number ${items.receipt_number} ${APPROVED}`);
          setStatusOnGrid(
            items.receipt_number,
            APPROVED,
            items.employee_date,
            items.employee_total
          );
          fetchReceiptsList();
        })
        .catch(err => {
          error("Error occurred while approving the receipt");
          console.error(err);
        });
    });
  }, [state.selectedReceipt, state.comment, setStatusOnGrid, fetchReceiptsList]);

  const handleReject = useCallback(() => {
    if (state.selectedReceipt.length === 0) {
      warning("Please select a receipt");
      return;
    }

    state.selectedReceipt.forEach(items => {
      fetchWithJson(endpoint.dc_reject_receipt, {
        receipt_id: items.receipt_number,
        comments: state.comment,
      })
        .then(() => {
          error(`Receipt number ${items.receipt_number} ${REJECTED}`);
          setStatusOnGrid(items.receipt_number, REJECTED);
          fetchReceiptsList();
        })
        .catch(err => {
          error("Error occurred while rejecting the receipt");
          console.error(err);
        });
    });
  }, [state.selectedReceipt, state.comment, setStatusOnGrid, fetchReceiptsList]);

  const handleReset = useCallback(() => {
    if (state.selectedReceipt.length === 0) {
      warning("Please select a receipt");
      return;
    }

    state.selectedReceipt.forEach(items => {
      fetchWithJson(endpoint.dc_reset_receipt, {
        receipt_id: items.receipt_number,
      })
        .then(() => {
          warning(`Receipt number ${items.receipt_number} reset`);
          setStatusOnGrid(items.receipt_number, RESET);
          fetchReceiptsList();
        })
        .catch(err => {
          error("Error occurred while re-setting the receipt status");
          console.error(err);
        });
    });
  }, [state.selectedReceipt, setStatusOnGrid, fetchReceiptsList]);

  const resetFilter = useCallback(() => {
    setState(prev => ({
      ...prev,
      receiptReferenceNo: 0,
      driverId: "",
      exp1: "",
      exp2: "",
      Approved: 0,
      Denyed: 0,
      New: 0,
      DateMatch: 0,
      TotalMatch: 0,
      orderNo: "",
      noOfFilter: 0,
    }));
  }, []);

  const handleSearchButton = useCallback((
    exp1,
    exp2,
    Approved,
    Denyed,
    New,
    driverId,
    orderNo,
    receiptReference,
    DateMatch,
    TotalMatch,
    noOfFilter
  ) => {
    setState(prev => ({
      ...prev,
      noOfFilter,
      exp1,
      exp2,
      Approved,
      Denyed,
      New,
      driverId,
      orderNo,
      receiptReferenceNo: receiptReference,
      DateMatch,
      TotalMatch,
      selectedReceipt: [],
    }));

    fetchReceiptsList(
      orderNo || null,
      driverId || null,
      DateMatch === 1 ? true : null,
      TotalMatch === 1 ? true : null,
      Approved === 1 ? true : null,
      Denyed === 1 ? true : null,
      New === 1 ? true : null,
      receiptReference ? parseInt(receiptReference, 10) : null,
      exp1 || null,
      exp1 && exp2 ? exp2 : null
    );
  }, [fetchReceiptsList]);

  const commentPopUp = useCallback((action) => (
    <div className="pa2">
      <label className="f5 w-100">
        Comment (optional, comments entered here will overwrite the previous comments - IF ANY)
      </label>
      <div className="mt2 w-100">
        <Textarea
          bordered 
          fullWidth 
          css={{br: 0, mt: 10}}
          rows={4}
          value={state.comment}
          onChange={(e) => {
            if (e.target.value.length < 2000) {
              setState(prev => ({ ...prev, comment: e.target.value }));
            }
          }}
        />
        <div className="flex justify-end">
          <Button
            size='sm'
            auto
            css={{br: 0, mt: 10}}
            color={action === APPROVED ? "success" : "error"}
            onClick={action === APPROVED ? handleApproved : handleReject}
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  ), [state.comment, handleApproved, handleReject]);

  const onSelectionChanged = useCallback(() => {
    const selectedRows = gridApi.current.getSelectedRows();
    setState(prev => ({ ...prev, selectedReceipt: selectedRows }));
  }, []);

  const rowDoubleClicked = useCallback((row) => {
    setState(prev => ({ ...prev, isOpen: true, currentReceipt: row.data }));
  }, []);

  const onCancel = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  useEffect(() => {
    checkAccess(clientId);
    fetchReceiptsList();
  }, [clientId, fetchReceiptsList]);

  if (Access.allow_receipt_management !== 1) {
    return (
      <div>
        <div className="flex justify-between">
          <h4 className="f3 fw4 mb1 pt1 pb3 ph1 mt2">
            Reimbursable Receipts Management
          </h4>
        </div>
        <div className="ml2 mt3 f5">This feature is unavailable</div>
      </div>
    );
  }

  const height = window.innerHeight;
  const receipt_number_text = `Details for Receipt #${state.currentReceipt.receipt_number}`;
  const gridContainerStyle = {
    height: (height - 150) + "px",
  };

  return (
    <div className="">
      <div className="flex justify-between border-y-2 border-dotted p-4">
        <SubPageHeader>Reimbursable Receipts Management</SubPageHeader>
        <div className="flex justify-end">
          <Tooltip 
            css={{br: 0, pa: 10}}
            placement="bottom" 
            content={<div>{commentPopUp("Reject")}</div>} 
            trigger='click'>
            <Button
              css={{br: 0, mr: 10}}
              auto
              size='sm'
              ghost
              color='error'
            >
              Reject
            </Button>
          </Tooltip>

          <Button
            css={{br: 0, mr: 10}}
            auto
            size='sm'
            ghost
            color='primary'
            onClick={handleReset}
          >
            Reset status
          </Button>

          <Tooltip 
            css={{br: 0, pa: 10}}
            placement="bottom" 
            content={<div>{commentPopUp("Approved")}</div>} 
            trigger='click'>
            <Button
              css={{br: 0, mr: 10}}
              auto
              size='sm'
              ghost
              color='success'
            >
              Adopt Driver Data
            </Button>
          </Tooltip>

          {state.receiptsList.length !== 0 && (
            <Button
              css={{br: 0, mr: 10}}
              auto
              size='sm'
              ghost
              color='primary'
              onClick={() => {
                const csv = Papa.unparse(state.receiptDataForCsv, {
                  quotes: false,
                  quoteChar: '"',
                  escapeChar: '"',
                  delimiter: ",",
                  header: true,
                  newline: "\r\n",
                });
                
                if (!csv) return;
                
                const filename = "export_receipts.csv";
                const data = encodeURI("data:text/csv;charset=utf-8," + csv);
                const link = document.createElement("a");
                link.setAttribute("href", data);
                link.setAttribute("download", filename);
                document.getElementById("ReceiptManagementContainer").appendChild(link);
                link.click();
              }}
            >
              Export to CSV
            </Button>
          )}

          <Button
            css={{br: 0, mr: 10}}
            auto
            size='sm'
            color='secondary'
            onClick={() => {
              setState(prev => ({
                ...prev,
                showArchiveReceipt: !prev.showArchiveReceipt,
                receiptsList: !prev.showArchiveReceipt ? prev.archiveReceipts : prev.normalReceipts
              }));
            }}
          >
            {state.showArchiveReceipt ? "Show Active Receipts" : "Show Archived Receipts"}
          </Button>

          <div id="ReceiptManagementContainer" style={{ display: "none" }} />

          <Tooltip 
            css={{br: 0}}
            trigger='click'
            placement="bottom"
            content={
              <FilterReceipt
                handleSearchButton={handleSearchButton}
                exp1={state.exp1}
                exp2={state.exp2}
                Approved={state.Approved}
                receiptReference={state.receiptReferenceNo}
                driverId={state.driverId}
                Denyed={state.Denyed}
                New={state.New}
                DateMatch={state.DateMatch}
                TotalMatch={state.TotalMatch}
                orderNo={state.orderNo}
                clicked={state.fetching}
              />
            }
          >
            <Button
              css={{br: 0, mr: 10}}
              auto
              size='sm'
            >
              Choose Filter ({state.noOfFilter})
            </Button>
          </Tooltip>
        </div>
      </div>

      {state.fetching ? (
        <div className="flex justify-center mt6">
          <Loading size="lg">Fetching receipts..Hang on!</Loading>
        </div>
      ) : (
        <div className="mr2 mt2">
          {state.receiptsList.length === 0 ? (
            <div className="mt3 f5">No receipts found.</div>
          ) : (
            <div style={gridContainerStyle} className="ag-theme-quartz p-4">
              <AgGridReact
                columnDefs={state.columnDefs}
                rowData={state.receiptsList}
                rowMultiSelectWithClick={true}
                pagination={true}
                paginationAutoPageSize={true}
                onGridReady={onGridReady}
                rowSelection={state.rowSelection}
                onRowDoubleClicked={rowDoubleClicked}
                onSelectionChanged={onSelectionChanged}
                getRowId={(params) => params.data.receipt_number}
                enableColResize
                enableSorting
                enableFilter
              />
            </div>
          )}
        </div>
      )}

      <Modal 
        fullScreen
        closeButton
        aria-labelledby="receipt details"   
        aria-describedby="receipt details" 
        open={state.isOpen}
        onClose={onCancel}
      >
        <Modal.Header>
          <Text id="modal-title" size="1.5rem" b>
            {receipt_number_text}
          </Text>
        </Modal.Header>
        <Modal.Body>
          <ReimbursableRecipetsDetails
            setStatusOnGrid={setStatusOnGrid}
            currentReceipt={state.currentReceipt}
            fetchReceiptsList={fetchReceiptsList}
            resetFilter={resetFilter}
            closeAction={onCancel}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ReimbursableRecipetsManagement;